import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import {
  Search,
  FolderOpen,
  Trash2,
  Settings,
  Home
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import logoBlack from "@/assets/logo-black.svg";
import logoWhite from "@/assets/logo-white.svg";

interface SidebarProject {
  id: string;
  name: string;
  createdAt: string;
  updatedAt?: string;
}

type SidebarView = "create" | "projects";

interface SidebarProps {
  projects: SidebarProject[];
  activeProjectId: string | null;
  onSelectProject: (id: string) => void;
  onNewProject: () => void;
  onDeleteProject: (id: string) => void;
  isLoading?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  currentView?: SidebarView;
  onViewChange?: (view: SidebarView) => void;
}

export const Sidebar = ({
  projects,
  activeProjectId,
  onSelectProject,
  onDeleteProject,
  isLoading = false,
  currentView = "create",
  onViewChange,
}: SidebarProps) => {
  const [width, setWidth] = useState(280);
  const [isResizing, setIsResizing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const sidebarRef = useRef<HTMLDivElement>(null);

  const filteredProjects = projects.filter((p) =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort by most recent
  const sortedProjects = [...filteredProjects].sort((a, b) => {
    const dateA = new Date(a.updatedAt || a.createdAt);
    const dateB = new Date(b.updatedAt || b.createdAt);
    return dateB.getTime() - dateA.getTime();
  });

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const newWidth = e.clientX;
      if (newWidth >= 200 && newWidth <= 500) {
        setWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  return (
    <div
      ref={sidebarRef}
      className="h-full bg-card border-r border-border flex flex-col relative shrink-0"
      style={{ width: `${width}px` }}
    >
      {/* Resize Handle */}
      <div
        onMouseDown={handleMouseDown}
        className="absolute top-0 right-[-2px] h-full w-1 cursor-col-resize group z-20"
      >
        {/* Ligne visible au hover */}
        <div className="absolute top-0 right-0 h-full w-[2px] bg-hairline-strong transition-opacity opacity-0 group-hover:opacity-100" />
      </div>

      {/* Header avec logo */}
      <div className="px-4 h-12 flex items-center shrink-0">
        <button
          onClick={() => onViewChange?.("create")}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <img src={logoBlack} alt="Pixel" className="h-6 dark:hidden" />
          <img src={logoWhite} alt="Pixel" className="h-6 hidden dark:block" />
        </button>
      </div>

      {/* Divider */}
      <div className="px-4 pb-3">
        <div className="h-px bg-border" />
      </div>

      {/* Navigation */}
      <div className="px-3 space-y-0.5">
        <button
          className={cn(
            "w-full flex items-center gap-2 px-3 py-2 text-[13px] font-medium transition-colors",
            currentView === "create"
              ? "bg-secondary text-foreground"
              : "text-muted-foreground hover:bg-secondary hover:text-foreground"
          )}
          onClick={() => onViewChange?.("create")}
        >
          <Home className="w-4 h-4" />
          <span>Accueil</span>
        </button>
        <button
          className={cn(
            "w-full flex items-center gap-2 px-3 py-2 text-[13px] font-medium transition-colors",
            currentView === "projects"
              ? "bg-secondary text-foreground"
              : "text-muted-foreground hover:bg-secondary hover:text-foreground"
          )}
          onClick={() => onViewChange?.("projects")}
        >
          <FolderOpen className="w-4 h-4" />
          <span>Projets</span>
        </button>
      </div>


      {/* Divider */}
      <div className="px-4 py-3">
        <div className="h-px bg-border" />
      </div>

      {/* Section Chats récents */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="px-4 pb-2">
          <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-faint">Chats récents</span>
        </div>

        <ScrollArea className="flex-1 px-2">
          <div className="space-y-0.5">
            {sortedProjects.length === 0 ? (
              <p className="px-3 py-4 text-center font-mono text-[11px] text-faint">
                Aucun projet
              </p>
            ) : (
              sortedProjects.map((project) => (
                <div
                  key={project.id}
                  className={cn(
                    "group w-full flex items-center justify-between text-left px-3 py-2 transition-colors cursor-pointer",
                    activeProjectId === project.id
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  )}
                  onClick={() => onSelectProject(project.id)}
                >
                  <span className="truncate text-[13px] flex-1">
                    {project.name}
                  </span>
                  <button
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-faint hover:text-err"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteProject(project.id);
                    }}
                    aria-label="Supprimer le projet"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Search en bas */}
      <div className="p-3 border-t border-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-faint" />
          <Input
            placeholder="Rechercher…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-11 h-9 bg-secondary border-border text-[13px] text-foreground placeholder:text-faint"
          />
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 font-mono text-[10px] tracking-[0.08em] text-faint pointer-events-none">
            ⌘K
          </kbd>
        </div>
      </div>

      {/* Settings en bas */}
      <div className="p-3 border-t border-border">
        <button className="w-full flex items-center gap-2 px-3 py-2 text-[13px] font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
          <Settings className="w-4 h-4" />
          <span>Paramètres</span>
        </button>
      </div>
    </div>
  );
};
